'use client';

/**
 * Global Next.js error boundary.
 *
 * Captures any RSC throw that escapes a page or layout server component,
 * including ApiError from unguarded api-client calls. Without this
 * boundary the throw propagates into Next.js's worker-thread error path
 * and (on 2026-05-31, observed three times) can wedge the next-server
 * HTTP listener — process alive, port refusing connections, container
 * still reports `Up`. See:
 *   docs/solutions/tooling-decisions/studio-container-needs-healthcheck-for-wedged-next-server-2026-05-31.md
 *   docs/solutions/design-patterns/rsc-auxiliary-fetch-degrade-on-error-2026-05-04.md
 *
 * Rendering ANY page in place of the throw is the defense — the
 * framework now has a render product to flush instead of a hung stream.
 *
 * Route-group-specific error.tsx files (under (client)/, (operator)/,
 * (internal)/) take precedence for their respective groups; this file
 * is the root catch-all for /login, /legal/accept, /request-access,
 * and anything else outside the three tier groups.
 */
import * as React from 'react';
import { brand } from '@/lib/brand';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}): React.ReactElement {
  return (
    <div className="mx-auto max-w-xl px-6 py-12">
      <h1 className="text-xl font-semibold">Something went wrong.</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        The studio couldn&apos;t finish rendering this page. The error has been logged.
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        If this keeps happening, contact{' '}
        <a className="underline" href={`mailto:${brand.supportEmail}`}>
          {brand.supportEmail}
        </a>
        .
      </p>
      {error.digest ? (
        <p className="mt-2 font-mono text-xs text-muted-foreground">Reference: {error.digest}</p>
      ) : null}
      <div className="mt-6 flex gap-2">
        <button
          type="button"
          onClick={() => reset()}
          className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Try again
        </button>
        <a
          href="/"
          className="inline-flex h-9 items-center rounded-md border border-input bg-background px-4 text-sm font-medium hover:bg-accent"
        >
          Back to home
        </a>
      </div>
    </div>
  );
}
